/**
 * `QuickOnboardingDialog` — modal pour ajouter un nouveau collaborateur.
 *
 * Iter 81 · Deux modes explicites (choix au chargement) :
 *   - **STANDARD** (default) : envoie l'email d'onboarding → le candidat
 *     remplit sa fiche → puis contrat template → signature.
 *   - **RÉTROACTIF** : le collaborateur existe déjà (CDI signé début
 *     d'année, ancien contractuel, etc.). Aucun email envoyé, upload
 *     direct du PDF signé, matricule attribué immédiatement, apparition
 *     dans « Contrat signé ».
 *
 * Duplicate handling (STANDARD mode) — même comportement iter65 : panneau
 * in-dialog avec les 2 choix.
 */
import { AlertTriangle, ExternalLink, FileText, Loader2, Send, Upload, UserPlus, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";


const STATUT_OPTIONS = [
    {
        value: "cdi",
        label: "CDI",
        subtitle: "Durée indéterminée",
        description: "Aucune date de fin. Convient pour un poste permanent.",
        icon: "∞",
    },
    {
        value: "cdd",
        label: "CDD",
        subtitle: "Durée déterminée",
        description: "Date de fin OBLIGATOIRE. Convient pour une mission ponctuelle.",
        icon: "📅",
        requiresEndDate: true,
    },
    {
        value: "alternant",
        label: "Alternant",
        subtitle: "Contrat d'alternance",
        description: "Date de fin obligatoire (fin de la formation).",
        icon: "🎓",
        requiresEndDate: true,
    },
    {
        value: "assistant",
        label: "Assistant",
        subtitle: "Contrat d'assistant",
        description: "Date de fin obligatoire (fin de la mission).",
        icon: "🎯",
        requiresEndDate: true,
    },
];

const REGION_OPTIONS = [
    { value: "idf", label: "Île-de-France" },
    { value: "nouvelle_aquitaine", label: "Nouvelle-Aquitaine" },
    { value: "occitanie", label: "Occitanie" },
    { value: "paca", label: "PACA" },
];


export function QuickOnboardingDialog({ onCreated }) {
    const [open, setOpen] = useState(false);
    // "standard" (email fiche d'embauche) OR "retroactive" (upload PDF signé direct)
    const [mode, setMode] = useState("standard");

    // Shared fields
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [position, setPosition] = useState("");

    // Retro-only fields
    // iter83 · Aucun default sur statutEmploi → l'utilisateur DOIT choisir
    // explicitement (garde-fou UX : CDI vs CDD ne peut pas être oublié).
    const [statutEmploi, setStatutEmploi] = useState("");
    const [region, setRegion] = useState("idf");
    const [signedAt, setSignedAt] = useState(() => new Date().toISOString().slice(0, 10));
    const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState("");
    const [note, setNote] = useState("");
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const [busy, setBusy] = useState(false);
    const [roleOptions, setRoleOptions] = useState([]);
    const [duplicate, setDuplicate] = useState(null);

    useEffect(() => {
        if (!open || roleOptions.length > 0) return;
        (async () => {
            try {
                const { data } = await api.get("/contracts/options/list");
                const roles = Array.isArray(data?.roles) ? data.roles : [];
                setRoleOptions(
                    roles.map((r) => ({
                        key: r.key,
                        label: r.label_long || r.label,
                    })),
                );
            } catch {
                // Silent
            }
        })();
    }, [open, roleOptions.length]);

    function reset() {
        setMode("standard");
        setFirstName(""); setLastName(""); setEmail(""); setPhone("");
        setPosition("");
        setStatutEmploi("");
        setRegion("idf");
        const today = new Date().toISOString().slice(0, 10);
        setSignedAt(today);
        setStartDate(today);
        setEndDate("");
        setNote("");
        setFile(null);
        setDuplicate(null);
    }

    function pickFile(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.type && f.type !== "application/pdf") {
            toast.error("Format non supporté", { description: "Merci de fournir un PDF" });
            return;
        }
        if (f.size > 15 * 1024 * 1024) {
            toast.error("PDF trop lourd", { description: `${Math.round(f.size / 1024)} Ko > 15 Mo max` });
            return;
        }
        setFile(f);
    }

    async function _postCreateStandard(force) {
        setBusy(true);
        try {
            const url = `/candidates/quick-onboarding${force ? "?force=true" : ""}`;
            const { data } = await api.post(url, {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                email: email.trim().toLowerCase(),
                phone: phone.trim() || null,
                position: position.trim() || null,
            });
            const label = force
                ? `${data.first_name} ${data.last_name} — profil existant réutilisé, fiche envoyée.`
                : `${data.first_name} ${data.last_name} — email d'onboarding envoyé.`;
            toast.success(label);
            onCreated?.(data);
            reset();
            setOpen(false);
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (
                e.response?.status === 409
                && detail
                && typeof detail === "object"
                && detail.code === "duplicate_email"
                && detail.existing_candidate
            ) {
                setDuplicate(detail.existing_candidate);
            } else {
                const message = typeof detail === "string"
                    ? detail
                    : detail?.message || "Impossible de créer le candidat";
                toast.error(message);
            }
        } finally {
            setBusy(false);
        }
    }

    async function _postCreateRetroactive() {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("pdf", file);
            fd.append("first_name", firstName.trim());
            fd.append("last_name", lastName.trim());
            fd.append("email", email.trim().toLowerCase());
            if (phone.trim()) fd.append("phone", phone.trim());
            if (position.trim()) fd.append("position", position.trim());
            fd.append("statut_emploi", statutEmploi);
            fd.append("role", (position.trim() && matchRoleKey(position, roleOptions)) || "recruteur_donateurs");
            fd.append("region", region);
            fd.append("signed_at", signedAt);
            if (startDate) fd.append("start_date", startDate);
            if (endDate) fd.append("end_date", endDate);
            if (note.trim()) fd.append("note", note.trim());

            const { data } = await api.post(
                "/candidates/create-retroactive",
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success(`${data.first_name} ${data.last_name} — onboardé ✨`, {
                description: `Matricule attribué · Statut hired · Aucun email envoyé au candidat. Contrat ${statutEmploi.toUpperCase()}${endDate ? ` du ${formatFR(startDate)} au ${formatFR(endDate)}` : " à durée indéterminée"} signé le ${formatFR(signedAt)}.`,
                duration: 10000,
            });
            onCreated?.(data);
            reset();
            setOpen(false);
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (
                e.response?.status === 409
                && detail && typeof detail === "object"
                && detail.code === "duplicate_email"
            ) {
                setDuplicate(detail.existing_candidate);
            } else {
                const message = typeof detail === "string"
                    ? detail
                    : detail?.message || "Impossible d'onboarder le collaborateur";
                toast.error(message, { duration: 8000 });
            }
        } finally {
            setBusy(false);
        }
    }

    async function submit() {
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            toast.error("Prénom, nom et email sont requis");
            return;
        }
        if (!email.includes("@")) {
            toast.error("Adresse email invalide");
            return;
        }
        setDuplicate(null);
        if (mode === "retroactive") {
            if (!statutEmploi) {
                toast.error("Choisissez le type de contrat (CDI, CDD, Alternant ou Assistant)", {
                    description: "C'est le point le plus important : il détermine la date de fin.",
                });
                return;
            }
            const selectedOpt = STATUT_OPTIONS.find(o => o.value === statutEmploi);
            if (selectedOpt?.requiresEndDate && !endDate) {
                toast.error(`Un contrat ${selectedOpt.label} exige une date de fin`, {
                    description: "Renseignez la date de fin du contrat avant de continuer.",
                });
                return;
            }
            if (endDate && startDate && endDate < startDate) {
                toast.error("La date de fin doit être postérieure à la date de début");
                return;
            }
            if (!file) {
                toast.error("Sélectionnez le PDF du contrat signé");
                return;
            }
            await _postCreateRetroactive();
        } else {
            await _postCreateStandard(false);
        }
    }

    const dialogTitle = duplicate
        ? "Ce candidat existe déjà"
        : (mode === "retroactive"
            ? "Ajouter un collaborateur (contrat déjà signé)"
            : "Ajouter un candidat directement en onboarding");

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    data-testid="quick-onboarding-btn"
                    title="Créer un nouveau collaborateur — onboarding standard ou contrat déjà signé"
                    className="px-3 py-2 rounded-full border border-shariti-orange bg-shariti-orange text-white text-xs font-black uppercase tracking-widest transition hover:bg-shariti-orange/90 inline-flex items-center gap-1.5"
                >
                    <UserPlus className="w-4 h-4" />
                    Ajouter un candidat
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="quick-onboarding-dialog">
                <DialogHeader>
                    <DialogTitle>{dialogTitle}</DialogTitle>
                </DialogHeader>

                {duplicate ? (
                    <DuplicatePanel
                        candidate={duplicate}
                        onReuse={() => _postCreateStandard(true)}
                        onEditEmail={() => setDuplicate(null)}
                        busy={busy}
                        mode={mode}
                    />
                ) : (
                    <>
                        {/* Iter81 · Mode toggle — 2 pills */}
                        <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 p-1 bg-slate-50" data-testid="quick-onboarding-mode-toggle">
                            <button
                                type="button"
                                onClick={() => setMode("standard")}
                                data-testid="quick-onboarding-mode-standard"
                                className={`text-xs font-black uppercase tracking-widest px-3 py-2 rounded-lg transition inline-flex items-center justify-center gap-1.5 ${mode === "standard" ? "bg-white shadow text-shariti-black" : "text-slate-500 hover:text-shariti-black"}`}
                            >
                                <Send className="h-3 w-3" /> Onboarding standard
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("retroactive")}
                                data-testid="quick-onboarding-mode-retroactive"
                                className={`text-xs font-black uppercase tracking-widest px-3 py-2 rounded-lg transition inline-flex items-center justify-center gap-1.5 ${mode === "retroactive" ? "bg-white shadow text-shariti-black" : "text-slate-500 hover:text-shariti-black"}`}
                            >
                                <Zap className="h-3 w-3" /> Contrat déjà signé
                            </button>
                        </div>

                        <div className="space-y-4">
                            {mode === "standard" ? (
                                <p className="text-sm text-slate-600 leading-relaxed" data-testid="quick-onboarding-mode-hint-standard">
                                    Le candidat reçoit immédiatement l&apos;email de démarrage
                                    d&apos;onboarding avec le lien pour remplir sa fiche d&apos;embauche.
                                    Il apparaît dans la colonne <strong>Fiche envoyée</strong> du kanban.
                                </p>
                            ) : (
                                <p className="text-sm text-emerald-800 leading-relaxed rounded-lg bg-emerald-50 border border-emerald-200 p-2" data-testid="quick-onboarding-mode-hint-retro">
                                    <strong>Aucun email envoyé.</strong> Upload direct du PDF signé.
                                    Le collaborateur passe immédiatement en <strong>embauché</strong>,
                                    un matricule est attribué automatiquement, et il devient visible
                                    dans Booking / Reporting / PayFit. Idéal pour un CDI déjà signé
                                    en début d&apos;année.
                                </p>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="qo-first">Prénom</Label>
                                    <Input
                                        id="qo-first"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        placeholder="Alice"
                                        data-testid="quick-onboarding-first"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="qo-last">Nom</Label>
                                    <Input
                                        id="qo-last"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        placeholder="Dupont"
                                        data-testid="quick-onboarding-last"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="qo-email">Email</Label>
                                <Input
                                    id="qo-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="alice.dupont@example.com"
                                    data-testid="quick-onboarding-email"
                                />
                            </div>
                            <div>
                                <Label htmlFor="qo-phone">Téléphone <span className="text-slate-400 font-normal">(optionnel)</span></Label>
                                <Input
                                    id="qo-phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+33 6 12 34 56 78"
                                    data-testid="quick-onboarding-phone"
                                />
                            </div>
                            <div>
                                <Label htmlFor="qo-position">
                                    Poste{" "}
                                    <span className="text-slate-400 font-normal">
                                        {mode === "standard"
                                            ? "(sera affiché dans l'email d'embauche)"
                                            : "(inclut Responsable d'équipe, Chargé de développement…)"}
                                    </span>
                                </Label>
                                <Select value={position} onValueChange={setPosition}>
                                    <SelectTrigger id="qo-position" data-testid="quick-onboarding-position">
                                        <SelectValue placeholder="Choisir un poste…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {roleOptions.length === 0 ? (
                                            <SelectItem value="__loading__" disabled>
                                                Chargement…
                                            </SelectItem>
                                        ) : (
                                            roleOptions.map((r) => (
                                                <SelectItem
                                                    key={r.key}
                                                    value={r.label}
                                                    data-testid={`quick-onboarding-position-${r.key}`}
                                                >
                                                    {r.label}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Retro-only fields */}
                            {mode === "retroactive" && (
                                <>
                                    {/* iter83 · Garde-fou UX : sélecteur visuel du type de contrat.
                                        Aucun default → l'utilisateur DOIT cliquer, ce qui rend
                                        impossible d'oublier CDI vs CDD. */}
                                    <div data-testid="quick-onboarding-statut-picker">
                                        <Label className="flex items-center gap-2">
                                            Type de contrat <span className="text-red-500">*</span>
                                            <span className="text-[10px] font-black uppercase tracking-widest text-red-500 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                                                Obligatoire
                                            </span>
                                        </Label>
                                        <p className="text-[11px] text-slate-500 mt-1 mb-2">
                                            Choix critique — détermine si le contrat aura une date de fin (CDD/Alternant/Assistant) ou non (CDI).
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {STATUT_OPTIONS.map((o) => {
                                                const active = statutEmploi === o.value;
                                                return (
                                                    <button
                                                        key={o.value}
                                                        type="button"
                                                        onClick={() => {
                                                            setStatutEmploi(o.value);
                                                            if (!o.requiresEndDate) setEndDate("");
                                                        }}
                                                        data-testid={`quick-onboarding-statut-${o.value}`}
                                                        className={`text-left rounded-xl border-2 p-3 transition ${active
                                                            ? "border-shariti-orange bg-orange-50 shadow-sm"
                                                            : "border-slate-200 bg-white hover:border-slate-300"
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-xl leading-none">{o.icon}</span>
                                                            <span className={`text-sm font-black tracking-wide ${active ? "text-shariti-orange" : "text-shariti-black"}`}>
                                                                {o.label}
                                                            </span>
                                                        </div>
                                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                                                            {o.subtitle}
                                                        </div>
                                                        <div className="text-[11px] leading-snug text-slate-600">
                                                            {o.description}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <Label htmlFor="qo-region">Région</Label>
                                        <Select value={region} onValueChange={setRegion}>
                                            <SelectTrigger id="qo-region" data-testid="quick-onboarding-region">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {REGION_OPTIONS.map((o) => (
                                                    <SelectItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <Label htmlFor="qo-signed-at">Date de signature</Label>
                                            <Input
                                                id="qo-signed-at"
                                                type="date"
                                                value={signedAt}
                                                onChange={(e) => setSignedAt(e.target.value)}
                                                data-testid="quick-onboarding-signed-at"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="qo-start-date">Date de début</Label>
                                            <Input
                                                id="qo-start-date"
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                data-testid="quick-onboarding-start-date"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="qo-end-date" className="flex items-center gap-1">
                                                Date de fin
                                                {statutEmploi && STATUT_OPTIONS.find(o => o.value === statutEmploi)?.requiresEndDate && (
                                                    <span className="text-red-500">*</span>
                                                )}
                                            </Label>
                                            {statutEmploi === "cdi" ? (
                                                <div
                                                    className="h-10 px-3 rounded-md border border-slate-200 bg-slate-50 flex items-center text-xs text-slate-500 italic"
                                                    data-testid="quick-onboarding-end-date-cdi-note"
                                                >
                                                    Indéterminée
                                                </div>
                                            ) : (
                                                <Input
                                                    id="qo-end-date"
                                                    type="date"
                                                    value={endDate}
                                                    min={startDate || undefined}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    disabled={!statutEmploi}
                                                    data-testid="quick-onboarding-end-date"
                                                    className={!statutEmploi ? "opacity-50" : ""}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* iter83 · Récap visuel — confirme à l'admin ce qu'il va créer */}
                                    {statutEmploi && (
                                        <div
                                            className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 px-3 py-2.5 flex items-start gap-2"
                                            data-testid="quick-onboarding-summary"
                                        >
                                            <div className="text-lg leading-none pt-0.5">
                                                {STATUT_OPTIONS.find(o => o.value === statutEmploi)?.icon}
                                            </div>
                                            <div className="text-[12px] text-emerald-900 leading-relaxed">
                                                <div className="font-black">
                                                    Contrat <span className="uppercase">{statutEmploi}</span>
                                                    {statutEmploi === "cdi"
                                                        ? " à durée indéterminée"
                                                        : (endDate
                                                            ? ` du ${formatFR(startDate)} au ${formatFR(endDate)}`
                                                            : ` — à partir du ${formatFR(startDate)}, ⚠️ date de fin requise`
                                                        )}
                                                </div>
                                                <div className="text-[11px] text-emerald-800/80 mt-0.5">
                                                    Signé le {formatFR(signedAt)}. Matricule attribué automatiquement.
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <Label>Contrat signé (PDF)</Label>
                                        <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-3 hover:border-shariti-orange transition" data-testid="quick-onboarding-pdf-dropzone">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="application/pdf"
                                                onChange={pickFile}
                                                data-testid="quick-onboarding-pdf-input"
                                                className="hidden"
                                            />
                                            {file ? (
                                                <div className="flex items-center gap-3">
                                                    <FileText className="h-5 w-5 text-shariti-orange" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-bold text-shariti-black truncate">{file.name}</div>
                                                        <div className="text-[11px] text-slate-500">
                                                            {Math.round(file.size / 1024)} Ko
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFile(null)}
                                                        data-testid="quick-onboarding-pdf-clear"
                                                        className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-red-600"
                                                    >
                                                        Retirer
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    data-testid="quick-onboarding-pdf-pick"
                                                    className="w-full flex flex-col items-center gap-1 py-3 text-slate-500 hover:text-shariti-orange"
                                                >
                                                    <Upload className="h-5 w-5" />
                                                    <span className="text-sm font-bold">Sélectionner le PDF signé</span>
                                                    <span className="text-[10px] text-slate-400">Max 15 Mo · application/pdf</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <Label htmlFor="qo-note">Note interne <span className="text-slate-400 font-normal">(traçabilité URSSAF, optionnel)</span></Label>
                                        <Input
                                            id="qo-note"
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                            placeholder="Ex : CDI RE signé le 15/01/2026, contrat papier archivé RH."
                                            data-testid="quick-onboarding-note"
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
                            <Button
                                onClick={submit}
                                disabled={busy || (mode === "retroactive" && (!file || !statutEmploi || (STATUT_OPTIONS.find(o => o.value === statutEmploi)?.requiresEndDate && !endDate)))}
                                data-testid="quick-onboarding-confirm"
                            >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : (
                                    mode === "retroactive"
                                        ? <Zap className="h-4 w-4 mr-2" />
                                        : <Send className="h-4 w-4 mr-2" />
                                )}
                                {busy
                                    ? "Création…"
                                    : (mode === "retroactive"
                                        ? "Créer & attribuer matricule"
                                        : "Créer et envoyer la fiche")}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}


/** Best-effort mapping label → role key. */
function matchRoleKey(positionLabel, roleOptions) {
    const norm = (positionLabel || "").toLowerCase().trim();
    const opt = roleOptions.find((r) => r.label.toLowerCase() === norm);
    return opt?.key || null;
}

/** iter83 · Format date ISO → JJ/MM/AAAA pour le récap visuel. */
function formatFR(isoDate) {
    if (!isoDate) return "—";
    try {
        const [y, m, d] = isoDate.split("-");
        return `${d}/${m}/${y}`;
    } catch {
        return isoDate;
    }
}


/**
 * `DuplicatePanel` — the rich "email already exists" screen shown instead
 * of a red toast. Gives the admin two clear paths.
 */
function DuplicatePanel({ candidate, onReuse, onEditEmail, busy, mode }) {
    const fullName = `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim() || "—";
    // In retroactive mode, "réutiliser ce profil" is disabled — the admin
    // MUST open the existing fiche and use "Onboarder rétroactivement"
    // on the contract card (avoids creating a zombie double-onboarding).
    const reuseAllowed = mode !== "retroactive";
    return (
        <div className="space-y-4" data-testid="quick-onboarding-duplicate-panel">
            <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 leading-relaxed">
                    Un profil avec l&apos;email <strong>{candidate.email}</strong> existe déjà —
                    {reuseAllowed
                        ? " tu peux le réutiliser directement ou modifier l'email si c'est une autre personne."
                        : " ouvre sa fiche et utilise le bouton « Onboarder rétroactivement » sur sa carte de contrat."}
                </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                    Profil existant
                </div>
                <div className="text-base font-black text-shariti-black">{fullName}</div>
                <div className="text-xs font-medium text-slate-500 mt-0.5">
                    Statut actuel : <span className="font-semibold text-shariti-black">{candidate.status || "—"}</span>
                </div>
                {candidate.onboarding_sent_at && (
                    <div className="text-[11px] font-semibold text-slate-500 mt-1">
                        Fiche déjà envoyée le {new Date(candidate.onboarding_sent_at).toLocaleDateString("fr-FR")}
                    </div>
                )}
                <a
                    href={`/candidats/${candidate.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-shariti-orange hover:text-shariti-black mt-2"
                    data-testid="quick-onboarding-duplicate-open"
                >
                    Ouvrir la fiche <ExternalLink className="h-3 w-3" />
                </a>
            </div>

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                <Button
                    variant="ghost"
                    onClick={onEditEmail}
                    disabled={busy}
                    data-testid="quick-onboarding-duplicate-edit"
                >
                    Modifier l&apos;email
                </Button>
                {reuseAllowed && (
                    <Button
                        onClick={onReuse}
                        disabled={busy}
                        data-testid="quick-onboarding-duplicate-reuse"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <Send className="h-4 w-4 mr-2" />
                        )}
                        Réutiliser ce profil et envoyer la fiche
                    </Button>
                )}
            </DialogFooter>
        </div>
    );
}
